// Supabase client singleton.
//
// URL + anon key come from Vite env vars and ship into the bundle.
// The anon key is PUBLIC — it's the identity the unauthenticated
// browser uses to talk to Supabase; RLS policies do the actual
// authorisation. Never put the service-role key here; that lives in
// the Cloudflare Worker as a secret.
//
// If env vars are missing, we return null rather than throwing — the
// app still boots (local dev / offline mode), it just can't sign in.
// Consumers check `isAuthAvailable()` before showing auth UI.
//
// Implicit-flow recovery bridge: Supabase's resetPasswordForEmail
// emits #access_token=...&type=recovery URLs (implicit flow), but our
// client is configured for PKCE. The SDK's strict-mode init throws
// AuthPKCEGrantCodeExchangeError when it sees implicit tokens under
// a PKCE config, which silently kills the recovery flow. We capture
// + strip those tokens at module-load time (before any SDK init),
// then expose them via consumeCapturedRecovery so useSession can
// hand-feed them to the SDK via setSession.

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

export type CapturedRecovery = {
  access_token: string;
  refresh_token: string;
  /** 'recovery' for password-reset URLs; null/other for OAuth-style
   *  fragments. Used to decide whether to flip recoveryMode. */
  type: string | null;
};

let capturedRecovery: CapturedRecovery | null = null;

(function captureImplicitGrantFromUrl() {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash ?? '';
  if (!hash.includes('access_token=')) return;
  try {
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const access_token = params.get('access_token');
    const refresh_token = params.get('refresh_token');
    if (!access_token || !refresh_token) return;
    capturedRecovery = {
      access_token,
      refresh_token,
      type: params.get('type'),
    };
    // Strip the fragment so the SDK's PKCE-strict init doesn't throw
    // on first load. replaceState keeps history clean.
    const url = new URL(window.location.href);
    url.hash = '';
    window.history.replaceState({}, '', url.toString());
  } catch {
    // Best effort — if URLSearchParams parsing fails, fall through.
    // The SDK's own error path will surface whatever it would have.
  }
})();

/** Returns and clears any implicit-flow tokens captured at module
 *  load. useSession calls this on mount; subsequent calls return
 *  null. Single-use by design — these tokens are consumable, not
 *  observable state. */
export function consumeCapturedRecovery(): CapturedRecovery | null {
  const out = capturedRecovery;
  capturedRecovery = null;
  return out;
}

let cached: SupabaseClient | null = null;
let initAttempted = false;

export function getSupabase(): SupabaseClient | null {
  if (initAttempted) return cached;
  initAttempted = true;
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !key) {
    console.info(
      '[auth] Supabase env vars missing — cloud features disabled. ' +
        'Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local.',
    );
    return null;
  }
  cached = createClient(url, key, {
    auth: {
      // Persist session in localStorage across reloads.
      persistSession: true,
      // Use PKCE for the magic-link flow — rotates the refresh token
      // properly on the redirect back. Recovery URLs are implicit and
      // get bridged in via consumeCapturedRecovery above.
      flowType: 'pkce',
      // Refresh before the JWT expires so the Worker never sees a
      // stale token.
      autoRefreshToken: true,
    },
  });
  return cached;
}

export function isAuthAvailable(): boolean {
  return getSupabase() !== null;
}
